import { api_configuration } from "../../common";
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import api_cs = api_configuration.types.client_state.alan_api;
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import resetModifiedState from "../state/resetModifiedState";
import { DatastoreProxy } from "../interface/DatastoreProxy";
import { iCollection } from "../state/Collection";
import * as mapToIdPathBuilder from "../state/mapToIdPathBuilder";

if (window.requestIdleCallback === undefined) {
	window.requestIdleCallback = (callback, options) => {
		return setTimeout(callback, options?.timeout ?? 500);
	};

	window.cancelIdleCallback = (handle) => {
		clearTimeout(handle);
	};
}

export function hasMutationPermission(id_path: IdPathBuilder, callback: (has_permission: boolean) => void): void {
	DatastoreProxy.instance.subscribeOnce(
		{
			"context node": id_path.build(),
			"mutation permissions": ["include", {}],
			"node subscription": {
				attributes: {},
			},
		},
		(result) => {
			result.properties.context.switch({
				"does not exist": () => undefined,
				exists: ($) =>
					$.properties.subscription_result_data.properties.update_permitted.properties.permitted.switch({
						no: () => callback(false),
						yes: () => callback(true),
					}),
				"permission denied": () => callback(false),
			});
		}
	);
}

export function hasDeletePermission(id_path: IdPathBuilder, callback: (has_permission: boolean) => void): void {
	DatastoreProxy.instance.subscribeOnce(
		{
			"context node": id_path.build(),
			"mutation permissions": ["include", {}],
			"node subscription": {
				attributes: {},
			},
		},
		(result) => {
			callback(
				result.properties.context.switch({
					"does not exist": () => false,
					exists: ($) =>
						$.properties.delete_permitted.properties.permitted.switch({
							no: false,
							yes: true,
						}),
					"permission denied": false,
				})
			);
		}
	);
}

export function hasNodeMutationPermission(node: api_cs.Cnode, callback: (has_permission: boolean) => void): void {
	node.input
		.node_type()
		.input.value_source()
		.switch({
			derived: () => callback(false),
			base: () => hasMutationPermission(createIdPathBuilderFromNode(node), callback),
		});
}

export function hasCollectionCreationPermission(
	id_path: IdPathBuilder,
	collection_name: string,
	callback: (has_permission: boolean) => void
): void {
	DatastoreProxy.instance.subscribeOnce(
		{
			"context node": id_path.build(),
			"mutation permissions": ["include", {}],
			"node subscription": {
				attributes: {
					[collection_name]: {
						type: [
							"property",
							{
								type: [
									"collection",
									{
										"subscribe on": ["updates", {}],
										"include graph endpoints": ["no", {}],
									},
								],
							},
						],
					},
				},
			},
		},
		(results) => {
			callback(
				results.properties.context.switch<boolean>({
					"does not exist": false,
					exists: ($) =>
						$.properties.subscription_result_data.properties.read_permitted.switch<boolean>({
							no: false,
							yes: ($) =>
								$.properties.attributes.get(collection_name)!.properties.type.switch<boolean>({
									command: false,
									property: ($) =>
										$.properties.type.switch({
											collection: ($) =>
												$.properties.create_permitted.properties.permitted.switch({
													no: false,
													yes: true,
												}),
											file: false,
											group: false,
											number: false,
											"state group": false,
											text: false,
										}),
								}),
						}),
					"permission denied": false,
				})
			);
		}
	);
}

export function updateEntityPermission(node: api_cs.Cnode): void {
	node.input.entity().switch({
		root: undefined,
		entry: ($) => {
			iCollection($.parent).updateMutationPermissions();
		},
	});
}

const nodes_to_update = new Set<api_cs.Cnode>();
export function updatePermissions(node: api_cs.Cnode): void {
	if (nodes_to_update.has(node)) return;
	nodes_to_update.add(node);
	requestIdleCallback(() => {
		nodes_to_update.delete(node);
		if (node.destroyed) return;
		doUpdatePermissions(node);
	});
}

function doUpdatePermissions(node: api_cs.Cnode): void {
	if (node.destroyed) {
		return;
	}
	node.properties.location.switch({
		remote: () => {
			hasNodeMutationPermission(node, (has_permission) => {
				if (node.destroyed) {
					return;
				}
				node.properties.location.switch({
					remote: () => {
						if (!has_permission) {
							resetModifiedState(node, false);
						}
						node.properties.attributes.forEach(($) => {
							$.properties.type.switch<void>({
								operation: undefined,
								property: ($) =>
									$.inferences
										.property_type()
										.properties.type.state.node.output.value_source()
										.switch({
											derived: undefined,
											base: () =>
												$.properties.type.switch({
													collection: ($) => {
														const collection_name = $.parent.parent.key.entry;
														const id_path = createIdPathBuilderFromNode($.parent.parent.parent);
														hasCollectionCreationPermission(id_path, collection_name, (has_create_permission) => {
															if ($.destroyed) {
																return;
															}
															const mutable_collection = $;
															if (has_create_permission) {
																mutable_collection.properties.is_editable.ensure("yes", {
																	"edit mode": ["off", {}],
																});
																mutable_collection.properties.can_create_entry.ensure("yes", {});
															} else {
																$.properties.is_editable.ensure("no", {});
															}
															$.properties.entries.forEach(($) => {
																updatePermissions($.properties.node);
															});
														});
													},
													file: ($) => {
														if (has_permission) {
															$.properties.is_editable.ensure("yes", {
																modified: ["no", {}],
																"edit mode": ["off", {}],
															});
														} else {
															$.properties.is_editable.ensure("no", {});
														}
													},
													group: ($) => {
														updatePermissions($.properties.node);
													},
													number: ($) => {
														if (has_permission) {
															// this cast to elementary is safe becase the property type is already switched to elementary.
															$.inferences
																.number_type()
																.properties.type.cast("base")
																.properties.type.switch({
																	causal: undefined,
																	simple: () => {
																		$.properties.is_editable.ensure("yes", {
																			modified: ["no", {}],
																			"edit mode": ["off", {}],
																		});
																	},
																});
														} else {
															$.properties.is_editable.ensure("no", {});
														}
													},
													"state group": ($) => {
														$.properties.is_set.switch({
															no: undefined,
															yes: ($) => updatePermissions($.properties.state.ref.properties.node),
														});
													},
													text: ($) => {
														if (has_permission) {
															switch ($.properties.is_key.state.name) {
																case "yes": {
																	const collection = $.parent.parent.parent.input
																		.position()
																		.cast("entity")
																		.cast("entry").parent;
																	collection.properties.is_editable.switch({
																		no: () => {
																			$.properties.is_editable.ensure("no", {});
																		},
																		yes: () => {
																			hasDeletePermission(
																				createIdPathBuilderFromNode(node),
																				(has_delete_permissions) => {
																					if ($.destroyed) {
																						return;
																					}
																					if (has_delete_permissions) {
																						$.properties.is_editable.ensure("yes", {
																							modified: ["no", {}],
																							"edit mode": ["off", {}],
																						});
																					} else {
																						$.properties.is_editable.ensure("no", {});
																					}
																				}
																			);
																		},
																	});
																	break;
																}
																case "no":
																	$.properties.is_editable.ensure("yes", {
																		modified: ["no", {}],
																		"edit mode": ["off", {}],
																	});
																	break;
																default:
																	unexpectedState($.properties.is_key.state);
															}
														} else {
															$.properties.is_editable.ensure("no", {});
														}
													},
												}),
										}),
							});
						});
					},
					local: undefined,
				});
			});
		},
		local: undefined,
	});
}

const permissions_checked_on_entries = new WeakSet<api_cs.Centries__yes__has_result>();
export function entryPermissionChecked(entry: api_cs.Centries__yes__has_result) {
	permissions_checked_on_entries.add(entry);
}
export async function checkEntryPermission(entry: api_cs.Centries__yes__has_result) {
	if (permissions_checked_on_entries.has(entry)) return;
	entryPermissionChecked(entry);
	let id_path: IdPathBuilder | undefined;
	const initialized = entry.properties.entry_status.properties.initialized.state;
	switch (initialized.name) {
		case "no":
			id_path = mapToIdPathBuilder.fromClientState(initialized.node.properties.referenced_node);
			break;
		case "yes":
			id_path = createIdPathBuilderFromNode(initialized.node.properties.referenced_node.output.result_node());
			break;
		case "unknown":
			break;
	}
	if (id_path === undefined) return;
	const result = await DatastoreProxy.instance.subscribeOnce({
		"context node": id_path.build(),
		"node subscription": {
			attributes: {},
		},
		"mutation permissions": ["include", {}],
	});
	if (entry.destroyed) return;
	switch (result.properties.context.state.name) {
		case "does not exist": {
			entry.properties.is_editable.set("no", {});
			break;
		}
		case "exists": {
			const exists_context = result.properties.context.state.node;
			switch (exists_context.properties.delete_permitted.properties.permitted.state.name) {
				case "no": {
					entry.properties.is_editable.set("no", {});
					break;
				}
				case "yes": {
					entry.properties.is_editable.set("yes", {});
					break;
				}
			}
			break;
		}
		case "permission denied": {
			entry.properties.is_editable.set("no", {});
			break;
		}
	}
}
